import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { FormatDeContenu } from '@taki/sdk';

import type {
  AccesAuContenu,
  DepotDeContenus,
  EtatDeSauvegarde,
  Horloge,
  OptionsDeSauvegarde,
} from './moteur.js';
import { SauvegardeDeContenu } from './moteur.js';
import type { IdentiteDuPoste, StockageDuPoste } from './poste.js';
import { tiroirsDuPoste } from './poste.js';
import type { DelaisDeSauvegarde } from './regles.js';

/**
 * LE HOOK DE SAUVEGARDE, avec ses dependances EXPLICITES. Il cree un moteur
 * (`moteur.ts`) par contenu ouvert, l'ecoute, lui passe les droits, la
 * connexion et les evenements de la page, et le FERME quand l'ecran quitte le
 * contenu. Il ne decide rien lui-meme : tout ce qui se decide vit dans le
 * moteur et ses regles pures.
 *
 * POURQUOI DEUX HOOKS. Celui-ci ne lit ni le runtime ni le port d'acces : il les
 * recoit. C'est lui que les tests MONTENT, dans un vrai React, contre le vrai
 * SDK et les vraies rules de l'emulateur (`firebase/sauvegarde.test.ts`), et
 * contre un depot simule pour les courses (`tests/sauvegarde-monte.test.mjs`).
 * Une surface, elle, appelle `useContenuCanvas`, qui lit le runtime et l'acces
 * de la porte puis appelle celui-ci.
 */

export interface OptionsDuHook<T> {
  /** Le contenu a ouvrir ; `null` : rien d'ouvert. */
  id: string | null;
  /** Le sous-type que la surface edite (la porte `contents` / `type`). */
  type: string;
  format: FormatDeContenu<T>;
  ecrire(valeur: T): string;
  vignette?: (valeur: T) => Promise<string | null>;
  delais?: Partial<DelaisDeSauvegarde>;
}

export interface DependancesDuHook {
  contents: DepotDeContenus;
  orgId: string;
  /** Le compte et le projet ; `null` : rien ne se garde sur le poste (et l'ecran le dit). */
  identite: IdentiteDuPoste | null;
  stockage: StockageDuPoste | null;
  acces: AccesAuContenu;
  horloge?: Horloge;
  journal?: Pick<Console, 'error' | 'warn'>;
}

/** Ce que la surface recoit : l'etat du contenu, et ses gestes. */
export interface ContenuOuvert<T> extends EtatDeSauvegarde<T> {
  /** A CHAQUE changement du moteur de l'app. */
  signaler(valeur: T): void;
  enregistrerMaintenant(): Promise<void>;
  renommer(nom: string): Promise<void>;
  /** Conflit : ma version s'ecrit, l'autre est d'abord copiee en un contenu neuf. */
  garderMaVersion(): Promise<void>;
  /** Conflit : l'autre version s'affiche, la mienne devient un contenu neuf (ou reste sur le poste). */
  prendreLaLeur(): Promise<void>;
  /** Copie de secours : en place si possible, sinon en un contenu neuf ; rend son id. */
  restaurerCopie(): Promise<{ id: string; enPlace: boolean } | null>;
  jeterCopie(): void;
  /** Copie de secours : la telecharge en fichier (toujours possible, meme sans droits). */
  telechargerCopie(): void;
  reessayerOuverture(): Promise<void>;
  /** L'ecran a montre l'annonce du dernier geste. */
  oublierArrivee(): void;
}

/** Un id par chargement de page : deux onglets ne s'effacent pas leurs brouillons. */
const ONGLET = `o${Math.random().toString(36).slice(2, 10)}`;

/** L'etat d'avant toute ouverture (pas de contenu, ou moteur pas encore cree). */
function etatAuRepos<T>(chargement: boolean): EtatDeSauvegarde<T> {
  return Object.freeze({
    chargement,
    erreur: null,
    depart: null,
    nom: '',
    enregistrement: 'a-jour',
    lectureSeule: false,
    raison: null,
    conflit: null,
    copie: null,
    poste: null,
    horsLigne: false,
    occupe: false,
    erreurDeGeste: null,
    arrivee: null,
    peutCreer: false,
  });
}

const ABONNEMENT_VIDE = (): (() => void) => () => {};

/** Le nom d'un fichier telecharge : lisible, sans caractere refuse par un systeme de fichiers. */
function nomDeFichier(nom: string, mimeType: string): string {
  const propre = nom.replace(/[\\/:*?"<>|]+/g, ' ').trim().slice(0, 120) || 'contenu';
  const extension = mimeType === 'application/json' ? 'json' : mimeType.startsWith('text/markdown') ? 'md' : 'txt';
  return `${propre} (copie de secours).${extension}`;
}

export function useSauvegarde<T>(options: OptionsDuHook<T>, dependances: DependancesDuHook): ContenuOuvert<T> {
  const { id, type } = options;
  const { contents, orgId, identite, stockage, horloge, journal } = dependances;

  /* LES FONCTIONS DE L'APP changent d'identite a chaque rendu : le moteur lit
     toujours les dernieres, par ces getters, sans etre recree. */
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const accesRef = useRef(dependances.acces);
  accesRef.current = dependances.acces;

  const projet = identite?.projet ?? null;
  const compte = identite?.compte ?? null;
  const org = identite?.org ?? null;
  const [moteur, setMoteur] = useState<SauvegardeDeContenu<T> | null>(null);

  useEffect(() => {
    if (id === null) {
      setMoteur(null);
      return;
    }
    const lu = optionsRef.current;
    const optionsDuMoteur: OptionsDeSauvegarde<T> = {
      id,
      type,
      orgId,
      get format() {
        return optionsRef.current.format;
      },
      ecrire: (valeur) => optionsRef.current.ecrire(valeur),
      ...(lu.vignette !== undefined
        ? { vignette: (valeur: T) => optionsRef.current.vignette?.(valeur) ?? Promise.resolve(null) }
        : {}),
      ...(lu.delais !== undefined ? { delais: lu.delais } : {}),
    };
    const ident = projet !== null && compte !== null && org !== null ? { projet, compte, org } : null;
    const nouveau = new SauvegardeDeContenu<T>(optionsDuMoteur, {
      contents,
      tiroirs: tiroirsDuPoste(stockage, ident, id, ONGLET, horloge ? () => horloge.maintenant() : undefined),
      acces: accesRef.current,
      enLigne: typeof navigator === 'undefined' ? true : navigator.onLine,
      ...(horloge !== undefined ? { horloge } : {}),
      ...(journal !== undefined ? { journal } : {}),
    });
    setMoteur(nouveau);
    void nouveau.ouvrir();
    return () => nouveau.fermer();
  }, [id, type, orgId, contents, stockage, projet, compte, org, horloge, journal]);

  /* LES DROITS DE LA PORTE, suivis : leur retrait met le travail de cote. */
  const { peutEcrire, raison } = dependances.acces;
  useEffect(() => {
    moteur?.poserAcces({ peutEcrire, raison });
  }, [moteur, peutEcrire, raison]);

  /* LA CONNEXION ET LA SORTIE DE PAGE. */
  useEffect(() => {
    if (moteur === null || typeof window === 'undefined') return;
    const allume = (): void => moteur.poserEnLigne(true);
    const eteint = (): void => moteur.poserEnLigne(false);
    const avantDePartir = (evenement: BeforeUnloadEvent): void => {
      if (moteur.sauverAvantDePartir().retenir) {
        // Le travail n'est nulle part ailleurs qu'a l'ecran : on demande confirmation.
        evenement.preventDefault();
        evenement.returnValue = '';
      }
    };
    const pageQuittee = (): void => {
      moteur.sauverAvantDePartir();
    };
    const masquage = (): void => {
      if (document.visibilityState === 'hidden') moteur.sauverAvantDePartir();
    };
    window.addEventListener('online', allume);
    window.addEventListener('offline', eteint);
    window.addEventListener('beforeunload', avantDePartir);
    window.addEventListener('pagehide', pageQuittee);
    document.addEventListener('visibilitychange', masquage);
    return () => {
      window.removeEventListener('online', allume);
      window.removeEventListener('offline', eteint);
      window.removeEventListener('beforeunload', avantDePartir);
      window.removeEventListener('pagehide', pageQuittee);
      document.removeEventListener('visibilitychange', masquage);
    };
  }, [moteur]);

  const repos = useMemo(() => etatAuRepos<T>(id !== null), [id]);
  const etat = useSyncExternalStore(
    moteur?.abonner ?? ABONNEMENT_VIDE,
    moteur?.instantane ?? (() => repos),
    moteur?.instantane ?? (() => repos),
  );

  const signaler = useCallback((valeur: T) => moteur?.signaler(valeur), [moteur]);
  const enregistrerMaintenant = useCallback(() => moteur?.enregistrerMaintenant() ?? Promise.resolve(), [moteur]);
  const renommer = useCallback((nom: string) => moteur?.renommer(nom) ?? Promise.resolve(), [moteur]);
  const garderMaVersion = useCallback(() => moteur?.garderMaVersion() ?? Promise.resolve(), [moteur]);
  const prendreLaLeur = useCallback(() => moteur?.prendreLaLeur() ?? Promise.resolve(), [moteur]);
  const restaurerCopie = useCallback(() => moteur?.restaurerCopie() ?? Promise.resolve(null), [moteur]);
  const jeterCopie = useCallback(() => moteur?.jeterCopie(), [moteur]);
  const reessayerOuverture = useCallback(() => moteur?.reessayerOuverture() ?? Promise.resolve(), [moteur]);
  const oublierArrivee = useCallback(() => moteur?.oublierArrivee(), [moteur]);
  const telechargerCopie = useCallback(() => {
    const copie = moteur?.copieATelecharger();
    if (copie === null || copie === undefined || typeof document === 'undefined') return;
    const blob = new Blob([copie.texte], { type: copie.mimeType });
    const url = URL.createObjectURL(blob);
    const lien = document.createElement('a');
    lien.href = url;
    lien.download = nomDeFichier(copie.nom, copie.mimeType);
    document.body.appendChild(lien);
    lien.click();
    lien.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [moteur]);

  /* L'ETAT D'UN MOTEUR D'UN AUTRE CONTENU ne s'affiche jamais : entre le
     changement d'id et la creation du moteur suivant, on montre le repos. */
  const etatAffiche = moteur === null || id === null || moteur.contentId !== id ? repos : etat;

  return useMemo(
    () => ({
      ...etatAffiche,
      signaler,
      enregistrerMaintenant,
      renommer,
      garderMaVersion,
      prendreLaLeur,
      restaurerCopie,
      jeterCopie,
      telechargerCopie,
      reessayerOuverture,
      oublierArrivee,
    }),
    [
      etatAffiche,
      signaler,
      enregistrerMaintenant,
      renommer,
      garderMaVersion,
      prendreLaLeur,
      restaurerCopie,
      jeterCopie,
      telechargerCopie,
      reessayerOuverture,
      oublierArrivee,
    ],
  );
}
